import React, { useMemo, useState } from "react";
import BtwLabelPill, { getLabelColors, LABEL_COLOR_OPTIONS } from "./btw-label-pill";
import { adminCreateBtwLabel } from "../core/axios/endpoints";

const BtwLabelPicker = ({ selected = [], options = [], onChange, onLabelsChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("slate");
  const [createError, setCreateError] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);

  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

  const active = useMemo(
    () => options.filter((o) => !o.archived && selectedSet.has(String(o._id))),
    [options, selectedSet],
  );

  const available = useMemo(
    () => options.filter((o) => !o.archived),
    [options],
  );

  const apply = async (nextIds) => {
    if (!onChange) return;
    setBusy(true);
    try {
      await onChange(nextIds);
    } catch {
      // error wordt door parent geafficheerd; lokaal niets doen.
    } finally {
      setBusy(false);
    }
  };

  const toggle = (labelId) => {
    const idStr = String(labelId);
    const next = new Set(selectedSet);
    if (next.has(idStr)) next.delete(idStr);
    else next.add(idStr);
    apply(Array.from(next));
  };

  const remove = (labelId) => {
    const next = Array.from(selectedSet).filter((id) => id !== String(labelId));
    apply(next);
  };

  const submitNew = async () => {
    const name = newName.trim();
    if (!name) {
      setCreateError("Naam is verplicht.");
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await adminCreateBtwLabel({ name, color: newColor });
      const created = res?.data ?? res;
      // Reset form en sluit create-paneel.
      setNewName("");
      setNewColor("slate");
      setCreating(false);
      // Laat parent de label-lijst herladen zodat de nieuwe in de picker komt.
      onLabelsChanged?.();
      // Direct toepassen op deze aanvraag.
      if (created?._id) {
        const next = Array.from(new Set([...selected.map(String), String(created._id)]));
        await apply(next);
      }
    } catch (e) {
      setCreateError(e?.response?.data?.error ?? e?.message ?? "Aanmaken mislukt.");
    } finally {
      setCreateBusy(false);
    }
  };

  const closePopover = () => {
    setOpen(false);
    setCreating(false);
    setCreateError(null);
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-2">
        {active.length === 0 ? (
          <span className="text-sm text-gray-400">Geen labels.</span>
        ) : (
          active.map((label) => (
            <span key={label._id} className="inline-flex items-center">
              <BtwLabelPill label={label} />
              <button
                type="button"
                onClick={() => remove(label._id)}
                disabled={busy}
                className="-ml-1 rounded-full p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                aria-label={`Verwijder label ${label.name}`}
                title="Verwijderen"
              >
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          ))
        )}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-50"
        >
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Label
        </button>
      </div>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={closePopover}
            aria-hidden
          />
          <div className="absolute left-0 z-40 mt-2 w-72 rounded-lg bg-white p-1 shadow-lg ring-1 ring-gray-200">
            {!creating ? (
              <>
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Selecteer labels
                </p>
                <div className="max-h-72 overflow-y-auto">
                  {available.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-gray-400">
                      Nog geen labels.
                    </div>
                  ) : (
                    available.map((label) => {
                      const isActive = selectedSet.has(String(label._id));
                      const colors = getLabelColors(label.color);
                      return (
                        <button
                          key={label._id}
                          type="button"
                          onClick={() => toggle(label._id)}
                          disabled={busy}
                          className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm transition disabled:opacity-50 ${
                            isActive ? "bg-emerald-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${colors.dot}`} />
                            <span className="text-gray-800">{label.name}</span>
                          </span>
                          {isActive ? (
                            <svg
                              className="h-3.5 w-3.5 text-emerald-600"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="mt-1 border-t border-gray-100 pt-1">
                  <button
                    type="button"
                    onClick={() => setCreating(true)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-emerald-700 hover:bg-emerald-50"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Nieuw label aanmaken
                  </button>
                </div>
              </>
            ) : (
              <div className="p-2">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Nieuw label
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setCreateError(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    ← terug
                  </button>
                </div>
                <input
                  type="text"
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Bijv. Wacht op klant"
                  disabled={createBusy}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitNew();
                  }}
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {LABEL_COLOR_OPTIONS.map((c) => {
                    const colors = getLabelColors(c);
                    const sel = newColor === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        disabled={createBusy}
                        title={c}
                        className={`h-5 w-5 rounded-full ${colors.dot} ${
                          sel ? "ring-2 ring-offset-1 ring-gray-900" : "opacity-70 hover:opacity-100"
                        }`}
                      />
                    );
                  })}
                </div>
                {createError ? (
                  <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800">
                    {createError}
                  </div>
                ) : null}
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setCreateError(null);
                    }}
                    disabled={createBusy}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={submitNew}
                    disabled={createBusy || !newName.trim()}
                    className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {createBusy ? "Bezig…" : "Aanmaken & toepassen"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default BtwLabelPicker;
